import { useContext, useEffect, useState } from "react";
import { Route, Switch } from "react-router-dom";
import Blog from "../../containers/blog/Blog";
import OurStory from "../../containers/static-pages/OurStory";
import PrivacyPolicy from "../../containers/static-pages/PrivacyPolicy";
import TermsAndConditions from "../../containers/static-pages/TermsAndConditions";
import Footer from "../components/Footer";
import Header from "../components/Header";
import ScrollToTop from "../components/ScrollToTop";
import AuthContext from "../context/authContext/AuthContext";
import { ACTIONS } from "../context/authContext/Constants";
import PrivateRoute from "./PrivateRoute";
import PublicRoute from "./PublicRoute";
import ReactGA from "react-ga4";
import SideBar from "../components/SideBar";
import { getFlags } from "../services/flags/flags.service";
import { IUser } from "../interfaces/user.interface";
import { IFlagsResponse } from "../interfaces/flags.interface";
import lodash from "lodash";
import { getMessages } from "../services/messages/messages.service";
import { IMessage } from "../interfaces/messages.interface";
import MessageModal from "../components/MessageModal";
import { IntercomProvider } from "react-use-intercom";
import useLogoutAllTabs from "../hooks/logoutAllTabs";
import SubscriptionModal from "../../containers/letsPlay/SubscriptionModal";
import useUser from "../hooks/useUser";

// The admin app's origin — the same constant `AdminPortalWrapper` uses to build its iframe `src`
// and as the `targetOrigin` when it posts into it.
const REACT_APP_PORTAL_URL = process.env[`REACT_APP_PORTAL_URL`];

const WebRoutes = () => {
    const { dispatch, state } = useContext(AuthContext);
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");
    const intercomAppId = process.env.REACT_APP_INTERCOM_APP_ID || "";
    const [open, setOpen] = useState(false);
    const [showFreeTrialModal, setShowFreeTrialModal] = useState(false);
    const [flags, setFlags] = useState<IFlagsResponse>();
    const [messages, setMessages] = useState<IMessage[]>();
    const { logoutAllTabs } = useLogoutAllTabs();
    const { isFreeTrialEnded } = useUser(state?.user);

    // Named so the add and the remove are the same reference. The effect re-runs when dispatch or
    // logoutAllTabs change, so an unremoved listener would stack.
    // AHA_ADMIN_logout here and AHA_ADMIN_LOGOUT in AdminPortalWrapper are two different messages
    // going in opposite directions; the casing is intentional.
    const onAdminLogoutMessage = (event: MessageEvent) => {
        if (
            event.origin === REACT_APP_PORTAL_URL &&
            event.data === "AHA_ADMIN_logout"
        ) {
            dispatch({ type: ACTIONS.LOGOUT });
        }
    };

    useEffect(() => {
        window.addEventListener("message", onAdminLogoutMessage);

        const triggerLogout = () => {
            setTimeout(() => {
                dispatch({ type: ACTIONS.LOGOUT });
            }, 500);
        };

        logoutAllTabs(triggerLogout);

        return () => {
            window.removeEventListener("message", onAdminLogoutMessage);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, logoutAllTabs]);

    const handleCloseSideBar = () => {
        setOpen(false);
    };

    const handleOpenSideBar = () => {
        setOpen(true);
    };

    const handleCloseRemindersModal = () => {
        setShowFreeTrialModal(false);
    };

    const handleCloseMessageModal = () => {
        setMessages(undefined);
    };

    useEffect(() => {
        const updateStoredUser = (newUser: IUser) => {
            dispatch({
                type: ACTIONS.SET_USER,
                user: { ...newUser },
            });
        };

        const handleFlags = async (user: IUser) => {
            const { response } = await getFlags();

            if (response) {
                const equals = lodash.isEqual(user, response.user);

                if (!equals) {
                    let updatedUser: IUser = { ...response.user };
                    if (user.parentInfo?.subRole) {
                        updatedUser.parentInfo = {
                            ...updatedUser.parentInfo,
                            subRole: user.parentInfo.subRole,
                        };
                    }

                    //update local storage user
                    updateStoredUser(updatedUser);
                }

                setFlags(response);

                if (
                    response.show3DaysReminding ||
                    response.show7DaysReminding ||
                    response.showLastReminding ||
                    response.showFreeTrialEndReminding
                ) {
                    setShowFreeTrialModal(true);
                }
            }
        };

        const handleMessages = async (user: IUser) => {
            const { response } = await getMessages();

            if (response && response.length) {
                setMessages(response);
            }
        };

        const handleInit = async (user: IUser) => {
            if (user) {
                await handleFlags(user);
                await handleMessages(user);
            }
        };

        if (user) {
            handleInit(JSON.parse(user));
        }
    }, [user, dispatch]);

    useEffect(() => {
        if (token && user) {
            dispatch({ type: ACTIONS.SET_TOKEN, token: JSON.parse(token) });
            dispatch({ type: ACTIONS.SET_USER, user: JSON.parse(user) });
        }
        dispatch({ type: ACTIONS.SET_LOADING, isLoading: false });
    }, [dispatch, token, user]);

    useEffect(() => {
        if (process.env.REACT_APP_ENV !== "local") {
            if (process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_ID) {
                ReactGA.initialize(
                    process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_ID
                );
                ReactGA.send({ hitType: "pageview", page: "/" });
            }
        }
    }, []);

    useEffect(() => {
        if (!state.user) {
            (window as any)?.HubSpotConversations?.clear({ resetWidget: true });
            setTimeout(() => {
                (window as any)?.HubSpotConversations?.widget?.remove();
            }, 500);
        }
    }, [state]);

    return (
        <div>
            <IntercomProvider
                appId={intercomAppId}
                autoBoot={true}
                autoBootProps={{
                    userId: state?.user?.id || "",
                    name: state.user
                        ? `${state.user.firstname ?? ""} ${
                              state.user.lastname ?? ""
                          }`
                        : "",
                    email: state?.user?.email,
                    customAttributes: { group_role: state?.user?.group_role },
                }}
            >
                {/* Keeps the footer at the bottom of the screen when a page is shorter than it. */}
                <div className="flex flex-col min-h-screen">
                    <Header
                        openSideBar={handleOpenSideBar}
                        isFreeTrialEnded={isFreeTrialEnded}
                    />
                    <div className="flex-1">
                        <ScrollToTop>
                            <Switch>
                                <Route path="/app" component={PrivateRoute} />

                                <Route
                                    exact
                                    path="/terms-and-conditions"
                                    component={TermsAndConditions}
                                />

                                <Route
                                    exact
                                    path="/privacy-policy"
                                    component={PrivacyPolicy}
                                />

                                <Route
                                    exact
                                    path="/our-story"
                                    component={OurStory}
                                />

                                <Route path="/blog" component={Blog} />

                                <Route path="/" component={PublicRoute} />
                            </Switch>
                        </ScrollToTop>
                    </div>
                    <Footer />
                </div>
                <SideBar open={open} onClose={handleCloseSideBar} />

                {/* FREE TRIAL MODAL */}
                {state.user && flags && (
                    <SubscriptionModal
                        showHeaderWording={true}
                        skipRoleDescription={true}
                        showModal={showFreeTrialModal}
                        onClose={() => {
                            if (!isFreeTrialEnded) {
                                handleCloseRemindersModal();
                            }
                        }}
                    />
                )}

                {/* MESSAGES MODAL */}
                {state.user && messages && (
                    <MessageModal
                        showModal={Boolean(messages)}
                        messages={messages}
                        onClose={handleCloseMessageModal}
                    />
                )}
            </IntercomProvider>
        </div>
    );
};

export default WebRoutes;
